import { Box, Divider, Stack, useTheme } from '@mui/material';
import type { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { animate, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { type MouseEvent, useCallback, useRef, useState } from 'react';
import {
  ColoredAvatar,
  DynamicIcon,
  EllipsesMenu,
  ShortcutText,
  Tooltip,
} from 'src/components';
import type { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { useSuppressSelection } from 'src/components/use-suppress-selection/use-suppress-selection';
import { appShortcuts } from 'src/constants/appShortcuts';
import { Notification, NotificationStatus } from 'src/graphql/entities/notification';
import {
  getNotificationConversationFriendlyId,
  getNotificationConversationSubject,
  getNotificationEntityName,
} from 'src/graphql/entities/notification/notification-target';
import { EntityNames } from 'src/graphql/types';
import { formatLastMessageSinceTime } from 'src/utils/date';
import { getThemeColor } from 'src/utils/strings';
import { useScreenDimensions } from 'src/utils/system';
import { LongPressEventType, useLongPress } from 'use-long-press';
import {
  INBOX_PANE_ITEM_ACTIONS_TEST_ID,
  INBOX_PANE_ITEM_META_TEST_ID,
  INBOX_PANE_ITEM_SNIPPET_TEST_ID,
  INBOX_PANE_ITEM_TEST_ID,
  INBOX_PANE_ITEM_TOOLTIP_MESSAGE_TEST_ID,
  INBOX_PANE_ITEM_UNREAD_DOT_TEST_ID,
  ROW_MENU_LONG_PRESS_MOVE_TOLERANCE_PX,
  ROW_MENU_LONG_PRESS_MS,
  SNIPPET_TOOLTIP_ENTER_DELAY_MS,
  SWIPE_ARCHIVE_THRESHOLD_PX,
} from './inbox-pane.constants';
import {
  ArchiveRevealBackground,
  AvatarCell,
  ConversationId,
  ItemRow,
  Meta,
  MotionSwipeable,
  RightCol,
  ROW_MENU_CLASS,
  ROW_META_CLASS,
  ROW_TEXT_CLASS,
  RowMenuSlot,
  RowMeta,
  Snippet,
  SubjectLine,
  SubjectText,
  SwipeContainer,
  UnreadDot,
} from './inbox-pane.styles';

// entityStatus.colorPath comes from statusDisplayPropsMap (primary/secondary/
// tertiary "system" roles); the mockup wants those grouped into its own
// open/waiting/done glyph palette instead of the raw system colors.
const STATUS_COLOR_PATH_TO_GLYPH_COLOR: Record<string, string> = {
  'custom.OV.sys.primary': 'custom.OV.sys.primary',
  'custom.OV.sys.secondary': 'custom.OV.extended.Signature.color',
  'custom.OV.sys.tertiary': 'custom.OV.sys.success',
};

export const InboxPaneItem = ({
  notification,
  isSelected,
  isActive,
  menuItems,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onSwipeArchive,
}: {
  notification: Notification;
  isSelected: boolean;
  isActive: boolean;
  menuItems?: EllipsesMenuOption[];
  onClick: (event: MouseEvent<HTMLElement>) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onSwipeArchive?: () => void;
}) => {
  const theme = useTheme();
  const { isTouchDevice } = useScreenDimensions();
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const rippleRef = useRef<TouchRippleActions | null>(null);

  // ButtonBase clears its ripple only on a touch-end over the row; a long press ends on
  // the menu backdrop and a swipe ends mid-drag, so both would leave it stuck on screen.
  const stopRipple = useCallback(() => rippleRef.current?.stop(), []);

  const hasMenu = !!menuItems?.length;
  const enableSwipe = isTouchDevice && !!onSwipeArchive;

  const [isSwiping, setIsSwiping] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [isRowMenuOpen, setIsRowMenuOpen] = useState(false);

  useSuppressSelection(isLongPressMenuOpen);

  const x = useMotionValue(0);
  const revealOpacity = useTransform(x, [-SWIPE_ARCHIVE_THRESHOLD_PX, 0], [1, 0], {
    clamp: true,
  });

  const handleDragEnd = (_: PointerEvent, info: PanInfo) => {
    setIsSwiping(false);
    if (info.offset.x <= -SWIPE_ARCHIVE_THRESHOLD_PX) {
      // archive only after the fly-out, so the list's exit animation composes from there
      animate(x, -window.innerWidth, { duration: 0.2 });
      requestAnimationFrame(() => onSwipeArchive?.());
    } else {
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 40 });
    }
  };

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // a swipe must not also register as a row click
      if (isSwiping) {
        return;
      }

      // React bubbles clicks from the row's portalled dropdown up to here — including the
      // backdrop click that closes it, which lands anywhere on screen
      if (!rowRef.current?.contains(event.target as Node)) {
        return;
      }

      onClick(event);
    },
    [isSwiping, onClick],
  );

  const longPressHandlers = useLongPress(
    hasMenu && isTouchDevice
      ? () => {
          stopRipple();
          setIsLongPressMenuOpen(true);
        }
      : null,
    {
      threshold: ROW_MENU_LONG_PRESS_MS,
      detect: LongPressEventType.Touch,
      cancelOnMovement: ROW_MENU_LONG_PRESS_MOVE_TOLERANCE_PX,
    },
  );

  const isUnread = notification.status === NotificationStatus.Unread;
  const isConversation =
    getNotificationEntityName(notification.entity) === EntityNames.Conversation;
  const friendlyId = isConversation
    ? getNotificationConversationFriendlyId(notification)
    : null;
  const subject = isConversation
    ? getNotificationConversationSubject(notification)
    : notification.subject;
  const glyphColorPath =
    STATUS_COLOR_PATH_TO_GLYPH_COLOR[notification.entityStatus.colorPath] ??
    notification.entityStatus.colorPath;

  // the snippet alone is a ~15px hover target in a ~50px row, so the whole text
  // block opens the tooltip; `right` keeps it off the rows below in a 248px pane
  const textBlock = (
    <Meta className={ROW_TEXT_CLASS}>
      <SubjectLine isUnread={isUnread}>
        {!!isUnread && <UnreadDot data-testid={INBOX_PANE_ITEM_UNREAD_DOT_TEST_ID} />}
        {friendlyId ? <ConversationId>#{friendlyId}</ConversationId> : null}
        <SubjectText>{subject}</SubjectText>
      </SubjectLine>
      <Snippet data-testid={INBOX_PANE_ITEM_SNIPPET_TEST_ID}>{notification.title}</Snippet>
    </Meta>
  );

  return (
    <SwipeContainer>
      {!!enableSwipe && (
        <ArchiveRevealBackground style={{ opacity: revealOpacity }}>
          <DynamicIcon name="archive" />
        </ArchiveRevealBackground>
      )}

      <MotionSwipeable
        drag={enableSwipe ? 'x' : false}
        // lock to the dominant axis first, so scrolling the list can't drag rows sideways
        dragDirectionLock
        dragConstraints={{ left: -window.innerWidth, right: 0 }}
        dragElastic={{ left: 0.6, right: 0 }}
        dragMomentum={false}
        style={{ x }}
        onDragStart={() => {
          stopRipple();
          setIsSwiping(true);
        }}
        onDragEnd={handleDragEnd}
      >
        <ItemRow
          ref={rowRef}
          touchRippleRef={rippleRef}
          isSelected={isSelected}
          isActive={isActive}
          isRead={!isUnread}
          onClick={handleRowClick}
          onMouseEnter={onMouseEnter}
          // the open menu's backdrop takes the pointer off the row; stay the shortcut target
          onMouseLeave={() => !isRowMenuOpen && onMouseLeave()}
          data-testid={INBOX_PANE_ITEM_TEST_ID}
          data-selected={isSelected}
          data-active={isActive}
          {...longPressHandlers()}
        >
          <AvatarCell>
            <ColoredAvatar name={notification.author.fullName} />
          </AvatarCell>

          {/* hover-only: long-press on touch already opens the row menu, and MUI's
              default touch listener would fight that gesture */}
          {isTouchDevice ? (
            textBlock
          ) : (
            <Tooltip
              title={
                <Stack spacing={0.75}>
                  <Box data-testid={INBOX_PANE_ITEM_TOOLTIP_MESSAGE_TEST_ID}>
                    {notification.title}
                  </Box>
                  {/* hovering the row is what makes it the shortcut's target, so the hint
                      is only true while this tooltip is open */}
                  <Divider sx={{ borderColor: 'custom.OV.sys.outline' }} />
                  <ShortcutText
                    label="Archive"
                    shortcut={appShortcuts.inboxArchiveNotification.command}
                    size="small"
                  />
                </Stack>
              }
              placement="right"
              enterDelay={SNIPPET_TOOLTIP_ENTER_DELAY_MS}
              slotProps={{
                tooltip: { sx: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } },
              }}
            >
              {textBlock}
            </Tooltip>
          )}

          <RightCol>
            <RowMeta
              isMenuOpen={isRowMenuOpen}
              className={ROW_META_CLASS}
              data-testid={INBOX_PANE_ITEM_META_TEST_ID}
            >
              <DynamicIcon
                name={notification.entityStatus.icon}
                library={notification.entityStatus.library}
                size={16}
                sx={{ color: getThemeColor(glyphColorPath, theme) }}
              />
              <span>{formatLastMessageSinceTime(notification.createdAt)}</span>
            </RowMeta>

            {!!hasMenu && !isTouchDevice && (
              <RowMenuSlot
                isMenuOpen={isRowMenuOpen}
                className={ROW_MENU_CLASS}
                data-testid={INBOX_PANE_ITEM_ACTIONS_TEST_ID}
              >
                <V2ThemeProvider>
                  <EllipsesMenu
                    options={menuItems}
                    icon="more-vertical"
                    iconLibrary="lucide"
                    triggerTestId="inbox-pane-item-menu"
                    onOpenChange={setIsRowMenuOpen}
                    sx={{ width: '1.5rem', height: '1.5rem' }}
                  />
                </V2ThemeProvider>
              </RowMenuSlot>
            )}
          </RightCol>
        </ItemRow>
      </MotionSwipeable>

      {!!hasMenu && !!isTouchDevice && (
        <V2ThemeProvider>
          <EllipsesMenu
            options={menuItems}
            hideTrigger
            open={isLongPressMenuOpen}
            anchorEl={rowRef.current}
            onClose={() => setIsLongPressMenuOpen(false)}
          />
        </V2ThemeProvider>
      )}
    </SwipeContainer>
  );
};
